import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import Button from "../../button-v1";
import Spinner from "../../spinner";
import { getAllPatientReadings } from "../../../services/admin-case-manager/readings";
import generateOutOfRangePDF from "../../../pages/dashboards/case-manager/review-escalation/OutOfRangePDF";
import { formatReadingData, filterRecords, addRecordType, groupAndSortRecords } from "../../../utils/outOfRangeHelpers";
import { convertUTCToLocal } from "../../../utils/helper";
import logo from "../../../assets/images/toca_health_pdf_logo.png";
import styles from "./styles.module.css";
import { buildReportFilename, DOCTYPES } from "../../../utils/fileNaming";

const OutOfRangePDFView = () => {
  const [params] = useSearchParams();
  const [downloading, setDownloading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [completionPercentage, setCompletionPercentage] = useState(0);

  // Get filter/search params from URL
  const nameSearch = params.get("nameSearch") || "";
  const providerSearch = params.get("providerSearch") || "";
  const locationSearch = params.get("locationSearch") || "";
  const categorySearch = params.get("categorySearch") || "";
  const filter = params.get("filter") || "escalated"; // Get filter type (escalated/reviewed)
  const timeRange = params.get("timeRange") || "1week"; 

  const {
    data: readingsData,
    isLoading: isReadingsLoading,
  } = useQuery({
    queryKey: ["patientReadings", timeRange],
    queryFn: () => getAllPatientReadings(timeRange),
    staleTime: 0,
    cacheTime: 0,
  });


  useEffect(() => {
    if (readingsData) {
      setTableData(formatReadingData(readingsData) || []);
    }
  }, [readingsData]);

  useEffect(() => {
    // Calculate completion percentage
    const fetchPatients = async () => {
      try {
        const axios = (await import("axios")).default;
        const { BASE_URL } = await import("../../../utils/constants");
        const token = localStorage.getItem("authToken");
        const tenant = sessionStorage.getItem("acc_id");
        const url = `${BASE_URL}/patients/${tenant}/patients/?type=all`;
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const allPatients = response.data?.patients || [];
        const totalPatients = allPatients?.length;
        const outOfRangeCount = allPatients?.filter(
          (p) => p.out_of_range
        ).length;
        const outOfRangePercent =
          totalPatients > 0
            ? Math.round((outOfRangeCount / totalPatients) * 100)
            : 0;
        const completionPercent =
          totalPatients > 0 ? 100 - outOfRangePercent : 0;
        setCompletionPercentage(completionPercent);
      } catch (error) {
        console.error("Error fetching patients:", error);
      }
    };
    fetchPatients();
  }, []);

  const filteredData = tableData?.filter((row) => {
    const nameMatch =
      nameSearch === "" ||
      row.name?.toLowerCase().includes(nameSearch.toLowerCase());
    if (!nameMatch) return false;

    const providerMatch =
      providerSearch === "" ||
      row.provider?.toLowerCase().includes(providerSearch.toLowerCase());
    if (!providerMatch) return false;

    const locationMatch =
      locationSearch === "" ||
      row.location?.toLowerCase().includes(locationSearch.toLowerCase());
    if (!locationMatch) return false;

    const categoryMatch =
      categorySearch === "" ||
      (Array.isArray(row.category)
        ? row.category.some((cat) =>
            cat?.toLowerCase().includes(categorySearch.toLowerCase())
          )
        : row.category?.toLowerCase().includes(categorySearch.toLowerCase()));
    if (!categoryMatch) return false;

    // Filter based on tab selection (escalated or reviewed)
    if (filter === "escalated") {
      return row.esclated === true;
    } else if (filter === "reviewed") {
      return row.reviewed === true;
    }
    
    // Default: show both (shouldn't happen with tab-based filtering)
    return row.esclated === true || row.reviewed === true;
  });

  const handleDownloadPDF = async () => {
    setDownloading(true);
    const toastId = toast.loading("Generating PDF...");
    try {
      // Get filtered data based on tab selection
      const allRecords = filterRecords(tableData, {
        nameSearch,
        providerSearch,
        locationSearch,
        categorySearch,
      });

      // Filter by escalated or reviewed based on filter param
      const filteredByType = allRecords.filter((row) => {
        if (filter === "escalated") {
          return row.esclated === true;
        } else if (filter === "reviewed") {
          return row.reviewed === true;
        }
        return row.esclated === true || row.reviewed === true;
      });

      // Add type field to each record
      const recordsWithType = addRecordType(filteredByType);

      // Group and sort records by patient name
      const sortedRecords = groupAndSortRecords(recordsWithType);

      // Set title based on filter type
      const title = filter === "escalated" 
        ? "Escalated Readings Report" 
        : filter === "reviewed"
        ? "Reviewed Readings Report"
        : "Reviewed and Escalated Readings Report";

      const organizationName = sessionStorage.getItem("acc_name") || null;

      // Use convertUTCToLocal to format the generated date (without seconds for PDF)
      const generatedDate = convertUTCToLocal(new Date().toISOString(), navigator.language, true);

      const pdf = generateOutOfRangePDF({
        logo,
        title,
        generatedDate,
        totalRecords: sortedRecords.length,
        completionPercentage,
        records: sortedRecords,
        organizationName,
        filter,
      });

      const fileName = buildReportFilename({
        doctype: filter === "escalated" ? DOCTYPES.ESCALATED_READINGS : DOCTYPES.REVIEWED_READINGS,
        program: "RPM",
        ext: "pdf",
      });
      pdf.save(fileName);
      toast.success("PDF Downloaded", { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate PDF", { id: toastId });
    } finally {
      setDownloading(false);
    }
  };

  if (isReadingsLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner />
      </div>
    );
  }

  // Get filtered records for preview
  const allRecords = filterRecords(tableData, {
    nameSearch,
    providerSearch,
    locationSearch,
    categorySearch,
  });

  // Filter by escalated or reviewed based on filter param
  const filteredByType = allRecords.filter((row) => {
    if (filter === "escalated") {
      return row.esclated === true;
    } else if (filter === "reviewed") {
      return row.reviewed === true;
    }
    return row.esclated === true || row.reviewed === true;
  });

  const recordsWithType = addRecordType(filteredByType);

  // Group and sort records by patient name
  const sortedRecordsWithType = groupAndSortRecords(recordsWithType);

  return (
    <>
      <section className={styles.download_button}>
        <Button loading={downloading} onClick={handleDownloadPDF}>
          Download PDF
        </Button>
      </section>

      <main className={styles.container}>
        <section className={styles.header}>
          <img src={logo} alt="Logo" className={styles.logo} />
          <div className={styles.separator}></div>
          {sessionStorage.getItem("acc_name") && (
            <p className={styles.organizationName}>{sessionStorage.getItem("acc_name")}</p>
          )}
          <div className={styles.titleRow}>
            <h1 className={styles.reportTitle}>
              {filter === "escalated" 
                ? "Escalated Readings Report" 
                : filter === "reviewed"
                ? "Reviewed Readings Report"
                : "Reviewed and Escalated Readings Report"}
            </h1>
          </div>
        </section>

        <section className={styles.reportInfo}>
          <div className={styles.infoRow}>
            <span className={styles.label}>Total Records:</span>
            <span className={styles.value}>{sortedRecordsWithType.length}</span>
          </div>
        </section>

        <section className={styles.table_container}>
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th>Patient Name</th>
                <th>Location</th>
                <th>Provider</th>
                <th>Reading Date</th>
                <th>Reading Type</th>
                <th>Reading</th>
                <th>Category</th>
                <th>{filter === "escalated" ? "Escalated At" : "Reviewed At"}</th>
                <th>Notes</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              {sortedRecordsWithType.length === 0 ? (
                <tr>
                  <td colSpan="10" className={styles.emptyCell}>
                    No records found
                  </td>
                </tr>
              ) : (
                sortedRecordsWithType.map((row, i) => (
                  <tr key={i} className={styles.tableRow}>
                    <td className={styles.tableCell}>{row.name || "_ _"}</td>
                    <td className={styles.tableCell}>
                      {row.location || "_ _"}
                    </td>
                    <td className={styles.tableCell}>
                      {row.provider || "_ _"}
                    </td>
                    <td className={styles.tableCell}>{row.date || "_ _"}</td>
                    <td className={styles.tableCell}>{row.type || "_ _"}</td>
                    <td className={styles.tableCell}>
                      {row.reading_value || "_ _"}
                    </td>
                    <td className={styles.tableCell}>
                      {Array.isArray(row.category)
                        ? row.category.join(", ")
                        : row.category || "_ _"}
                    </td>
                    <td className={styles.tableCell}>
                      {row.escalated_reviewed_at || "_ _"}
                    </td>
                    <td className={styles.tableCell}>
                      {row.recordType === "Escalated"
                        ? row.escalation_note ||
                          row.escalation_notes ||
                          row.note ||
                          row.notes ||
                          "_ _"
                        : row.review_note ||
                          row.review_notes ||
                          row.note ||
                          row.notes ||
                          row.interaction ||
                          "_ _"}
                    </td>
                    <td className={styles.tableCell}>
                      {row.recordType || "_ _"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>

        {/* FOOTER */}
            <footer className={styles.footer}>
              <div className={styles.footerLeft}>
                {(() => {
                  const reportTitle = filter === "escalated" 
                    ? "Escalated  Readings Report" 
                    : filter === "reviewed"
                    ? "Reviewed  Readings  Report"
                    : "Reviewed and Escalated Readings Report";
                  return sessionStorage.getItem("acc_name")
                    ? `${sessionStorage.getItem("acc_name")} - ${reportTitle}`
                    : reportTitle;
                })()}
              </div>
              <div className={styles.footerCenter}>
                Page 1
              </div>
              <div className={styles.footerRight}>
                 {convertUTCToLocal(new Date().toISOString(), navigator.language, true)}
              </div>
            </footer>
      </main>
    </>
  );
};

export default OutOfRangePDFView;

